import type { MenuProps } from 'antd';
import { App, Badge, Breadcrumb, Button, Dropdown, Segmented, Select, Tooltip } from 'antd';
import {
  type BreadcrumbItemType,
  type BreadcrumbSeparatorType,
} from 'antd/lib/breadcrumb/Breadcrumb';
import clsx from 'clsx';
import React, { type Key, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileCategoryEnum } from '../../../enums/FileCategoryEnum';
import { FileMenuActionEnum } from '../../../enums/FileMenuActionEnum';
import { FilesTableEnum } from '../../../enums/FilesTableEnum';
import { useAuth } from '../../../hooks/useAuth';
import { useExportFolderFileList } from '../../../hooks/useExportFolderFileList';
import { useGuardedRightPanel } from '../../../hooks/useGuardedRightPanel';
import { useTags } from '../../../hooks/useTags';
import { isLockedPasswordFolder } from '../../../lib/fileActions';
import type File from '../../../interfaces/File';
import type FileAction from '../../../interfaces/FileAction';
import type { GetFilesPayload } from '../../../interfaces/FileApiService';
import { useAppStore } from '../../../store/store';
import { truncateExportFolderFileListTitleFolder } from '../../../utils/exportFolderFileListCsv/exportFolderFileListCsv';
import { ticksToMs } from '../../../utils/utils';
import AiDocumentChat from '../../Ai/AiDocumentChat';
import AiSummary from '../../Ai/AiSummary';
import UserMultiSelect from '../../Common/UserMultiSelect';
import { Icon } from '../../Core/Icon';
import ExportFolderFileListForm from '../../Forms/ExportFolderFileListForm';
import FileDetail from '../FileDetail';
import FilesActions from '../FilesActions';
import { resolveFilesRightPanelAction } from '../filesRightPanelSync';

type FilterConfig = {
  key: string;
  /** left/right = desktop bar; dropdown = ListFilter panel (Activity Log pattern). */
  group: 'left' | 'right' | 'dropdown';
  /**
   * Left filters: hide from the desktop bar below this breakpoint and show inside the
   * filters dropdown instead (same progressive collapse as Activity Log).
   */
  hideInlineBelow?: 'xl';
  show: boolean;
  render: (className: string) => React.ReactNode;
};

interface FilesHeaderProps {
  isLoading: boolean;
  selectedFiles: File[];
  selectedTableKeys: Key[];
  setSelectedTableKeys: (keys: number[]) => void;
  isFilesListInGridMode: boolean;
  setIsFilesListInGridMode: (mode: boolean) => void;
  onFiltersChange: (filters: GetFilesPayload) => void;
  filters: GetFilesPayload;
  handleFileMenuAction: (filesIds: number[], action: FileMenuActionEnum) => void;
  breadcrumbItems: Partial<BreadcrumbItemType & BreadcrumbSeparatorType>[];
  section?: FilesTableEnum;
  filesActions: FileAction[];
  activeFiltersCount: number;
  filesCount?: number;
  parentFolder?: File | null;
}

const FilesHeader: React.FC<FilesHeaderProps> = ({
  selectedFiles,
  selectedTableKeys,
  setSelectedTableKeys,
  isFilesListInGridMode,
  setIsFilesListInGridMode,
  onFiltersChange,
  filters,
  handleFileMenuAction,
  breadcrumbItems,
  section,
  filesActions,
  activeFiltersCount,
  isLoading,
  filesCount = 0,
  parentFolder,
}) => {
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const { isInFileRoute } = useAuth(); // isAdminOrContributor, isReader
  const {
    isRightPanelOpen,
    guardedOpenRightPanel,
    guardedCloseRightPanel,
    guardedSetRightPanelContent,
  } = useGuardedRightPanel();
  const { startExport, isExportRunning } = useExportFolderFileList();
  const { tags } = useTags();
  const rightPanelKind = useAppStore((s) => s.rightPanelKind);
  const setRightPanelKind = useAppStore((s) => s.setRightPanelKind);
  const isViewerDrawerOpen = useAppStore((s) => s.isViewerDrawerOpen);
  const viewerDrawerFile = useAppStore((s) => s.viewerDrawerFile);
  const guardedSetRightPanelContentRef = useRef(guardedSetRightPanelContent);
  guardedSetRightPanelContentRef.current = guardedSetRightPanelContent;
  // Filename open used to leave selection empty; drawer file fills Details/Chat context.
  const panelFiles = useMemo(() => {
    if (selectedFiles.length > 0) return selectedFiles;
    if (isViewerDrawerOpen && viewerDrawerFile) return [viewerDrawerFile];
    return selectedFiles;
  }, [isViewerDrawerOpen, selectedFiles, viewerDrawerFile]);
  const selectedFileId = panelFiles.length === 1 ? (panelFiles[0]?.id ?? null) : null;
  const filePasswordSessions = useAppStore((s) => s.filePasswordSessions);
  const getFilePasswordSession = useAppStore((s) => s.getFilePasswordSession);
  const getFilePasswordSessionInfo = useAppStore((s) => s.getFilePasswordSessionInfo);
  const removeFilePasswordSession = useAppStore((s) => s.removeFilePasswordSession);

  const tagTitleById = useCallback(
    (tagId: number) => tags.find((tag) => tag.id === tagId)?.title,
    [tags],
  );

  // App modal.confirm + form footer (antd-modals). Not <Modal open=>.
  const handleOpenExportModal = (format: 'csv' | 'json' | 'excelXml') => {
    const folderLabel = truncateExportFolderFileListTitleFolder(
      parentFolder?.fileName?.trim() || t('files.exportFolderFileList.rootLabel'),
    );
    const modalInstance = modal.confirm({
      title: t('files.exportFolderFileList.modalTitle', { folder: folderLabel }),
      icon: null,
      centered: true,
      destroyOnHidden: true,
      footer: null,
      width: 480,
      content: (
        <ExportFolderFileListForm
          format={format}
          section={section}
          disabled={isExportRunning}
          onCancel={() => modalInstance.destroy()}
          onExport={({ columns, delimiter, tagsJoin, dateFormat }) => {
            modalInstance.destroy();
            void startExport({
              filters,
              folderName: parentFolder?.fileName ?? null,
              format,
              columns,
              delimiter,
              tagsJoin,
              dateFormat,
              tagTitleById,
            });
          }}
        />
      ),
    });
  };

  const exportMenuItems: MenuProps['items'] = [
    {
      key: 'csv',
      label: t('files.exportFolderFileList.formatCsv'),
      onClick: () => handleOpenExportModal('csv'),
    },
    {
      key: 'json',
      label: t('files.exportFolderFileList.formatJson'),
      onClick: () => handleOpenExportModal('json'),
    },
    {
      key: 'excelXml',
      label: t('files.exportFolderFileList.formatExcelXml'),
      onClick: () => handleOpenExportModal('excelXml'),
    },
  ];

  // Determines if the current selection is exactly one locked password folder
  // If true: disables the Details button and auto-closes the right panel
  const isLockedSingleSelection = useMemo(
    () =>
      selectedFiles.length === 1 &&
      selectedFiles[0] != null &&
      isLockedPasswordFolder(selectedFiles[0], getFilePasswordSession),
    [selectedFiles, getFilePasswordSession],
  );

  // State for refresh cooldown (in seconds) for the folder password (prevents rapid refreshes)
  const [folderPasswordRefreshCooldown, setFolderPasswordRefreshCooldown] = useState<number>(0);

  /**
   * Toggles visibility of the right file details panel
   */
  const handleToggleRightPanel = () => {
    void (async () => {
      if (isRightPanelOpen) {
        await guardedCloseRightPanel();
        return;
      }

      // Details button always opens FileDetail sync, not AI chat.
      setRightPanelKind('detail');
      guardedOpenRightPanel();
    })();
  };

  // Key used to distinguish between "sharedFrom" and "shareWith" filters
  const key = section === FilesTableEnum.Received ? 'sharedFrom' : 'shareWith';
  const isSharedSection = section === FilesTableEnum.Shared;
  const EXPIRING_WITHIN_DAYS_OPTIONS = [1, 3, 7, 14, 30, 60, 90] as const;

  // Counts per filter key for ListFilter badge (Activity Log isFilterActive pattern)
  const isFilterActive: Record<string, (f: GetFilesPayload) => number> = {
    fileTypes: (f) => (f.fileTypes?.length ? 1 : 0),
    shareWith: (f) => ((f.shareWith?.length || 0) + (f.sharedFrom?.length || 0) > 0 ? 1 : 0),
    onlyExpired: (f) => (f.onlyExpired === true ? 1 : 0),
    expiringWithinDays: (f) => (f.expiringWithinDays != null ? 1 : 0),
  };

  // Configuration for the filter UI options (type and share filters)
  const filterConfigs: FilterConfig[] = [
    {
      key: 'fileTypes',
      group: 'left',
      // Shared: collapse into ListFilter before shareWith on mid widths (Activity Log search pattern)
      hideInlineBelow: isSharedSection ? 'xl' : undefined,
      show: true,
      render: (className) => (
        <Select
          aria-label={t('a11y.fileTypesFilter')}
          className={className}
          allowClear
          mode="multiple"
          maxTagCount={1}
          showSearch={false}
          value={filters.fileTypes}
          placeholder={t('common.fileTypes')}
          onChange={(value) => {
            // On type change, reset paging offset
            onFiltersChange({
              ...filters,
              fileTypes: value,
              offset: 0,
            });
          }}
          options={[
            {
              label: t('common.documents'),
              value: FileCategoryEnum.Documents,
            },
            {
              label: t('common.images'),
              value: FileCategoryEnum.Images,
            },
            {
              label: t('common.audio'),
              value: FileCategoryEnum.Audio,
            },
            {
              label: t('common.video'),
              value: FileCategoryEnum.Video,
            },
            {
              label: t('common.other'),
              value: FileCategoryEnum.Other,
            },
          ]}
        />
      ),
    },
    {
      key: 'shareWith',
      group: 'left',
      show: true, // This can be further filtered by user roles if needed
      render: (className) => (
        <UserMultiSelect
          className={className}
          placeholder={`${t(section === FilesTableEnum.Received ? 'common.showFilesSharedBy' : 'common.showFilesSharedWith')}...`}
          includeGroups={section !== FilesTableEnum.Received}
          selectedUserIds={filters?.[key]
            ?.filter((user: { id: number; type: number }) => user.type === 0)
            .map((user: { id: number; type: number }) => user.id)}
          selectedGroupIds={filters?.shareWith
            ?.filter((user) => user.type === 1)
            .map((user) => user.id)}
          onUserChange={(ids: number[]) => {
            // Update selected users, preserving shared groups
            const users = ids.map((id: number) => ({ id, type: 0 }));

            onFiltersChange({
              ...filters,
              [key]: [...(filters?.[key] || []).filter((i) => i.type === 1), ...users],
              offset: 0,
            });
          }}
          additionalUserFilters={{
            shareFilter: section === FilesTableEnum.Received ? 3 : 1,
          }}
          additionalGroupFilters={{
            shareFilter: 1,
          }}
          onGroupChange={(ids: number[]) => {
            // Update selected groups, preserving shared users
            const groups = ids.map((id: number) => ({ id, type: 1 }));
            onFiltersChange({
              ...filters,
              shareWith: [...(filters?.shareWith || []).filter((i) => i.type === 0), ...groups],
              offset: 0,
            });
          }}
          onReset={() => {
            // Reset all share filters and paging
            onFiltersChange({
              ...filters,
              shareWith: [],
              sharedFrom: [],
              offset: 0,
            });
          }}
        />
      ),
    },
    {
      key: 'onlyExpired',
      group: 'dropdown',
      show: isSharedSection,
      render: (className) => (
        <Select
          aria-label={t('a11y.onlyExpiredFilter')}
          className={className}
          allowClear
          showSearch={false}
          value={filters.onlyExpired === true ? 'expired' : undefined}
          placeholder={t('common.onlyExpired')}
          onChange={(value: 'expired' | null) => {
            onFiltersChange({
              ...filters,
              onlyExpired: value === 'expired' ? true : undefined,
              offset: 0,
            });
          }}
          options={[
            {
              value: 'expired',
              label: t('common.expiredOnly'),
            },
          ]}
        />
      ),
    },
    {
      key: 'expiringWithinDays',
      group: 'dropdown',
      show: isSharedSection,
      render: (className) => (
        <Select
          aria-label={t('a11y.expiringWithinDaysFilter')}
          className={className}
          allowClear
          showSearch={false}
          value={filters.expiringWithinDays}
          placeholder={t('common.expiringWithinDays')}
          onChange={(value: number | null) => {
            onFiltersChange({
              ...filters,
              expiringWithinDays: value ?? undefined,
              offset: 0,
            });
          }}
          options={EXPIRING_WITHIN_DAYS_OPTIONS.map((value) => ({
            value,
            label: t('common.expiringWithinDaysOption', { count: value }),
          }))}
        />
      ),
    },
  ];

  /**
   * Desktop ListFilter badge: permanent dropdown filters + left filters that collapse
   * into the dropdown (Activity Log dropdownActiveFilters).
   */
  const dropdownActiveFilters = useMemo(() => {
    let dropdown = 0;
    filterConfigs.forEach((cfg) => {
      if (!cfg.show) return;
      const inc = isFilterActive[cfg.key]?.(filters) ?? 0;
      if (cfg.group === 'dropdown' || cfg.hideInlineBelow) dropdown += inc;
    });
    return dropdown;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, isSharedSection]);

  // Enables animation on the "Details" button for single selection
  const [isDetailsButtonAnimated, setIsDetailsButtonAnimated] = useState(false);

  /**
   * Returns current folder password session (if present) for the opened folder.
   * Used to check session expiration and trigger UI updates.
   */
  const currentFolderSession = useMemo(() => {
    if (!parentFolder?.id) return null;
    return getFilePasswordSessionInfo(parentFolder.id);
  }, [parentFolder?.id, filePasswordSessions, getFilePasswordSessionInfo]);

  /**
   * Effect: Triggers on folder session change.
   * If the password session for the current folder expires, remove session and trigger refresh.
   * (Timer countdown for this is visible in FilesTableTitleCell)
   */
  useEffect(() => {
    if (!currentFolderSession || !parentFolder?.id) return;

    const intervalId = window.setInterval(() => {
      const now = Date.now();
      const expiresAtMs = ticksToMs(currentFolderSession.expiresAt);
      const remainingMs = expiresAtMs - now;
      const remainingSec = Math.max(0, Math.floor(remainingMs / 1000));

      if (remainingSec <= 0) {
        removeFilePasswordSession(parentFolder.id);
        onFiltersChange({
          ...filters,
          offset: 0,
          refreshToken: Date.now(),
        });
      }
    }, 1000);

    // Clean up timer on unmount or change
    return () => {
      window.clearInterval(intervalId);
    };
  }, [currentFolderSession, parentFolder?.id, removeFilePasswordSession, onFiltersChange, filters]);

  /**
   * Effect: Manages decrementing the folder password refresh cooldown.
   * If cooldown is above 0, decrement every second.
   */
  useEffect(() => {
    if (folderPasswordRefreshCooldown <= 0) return;

    const intervalId = window.setInterval(() => {
      setFolderPasswordRefreshCooldown((prev) => {
        if (prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Clean up timer on unmount/change
    return () => {
      window.clearInterval(intervalId);
    };
  }, [folderPasswordRefreshCooldown]);

  /**
   * Effect: Manages animation of Details button.
   * If single file is selected and panel is closed, animate the button.
   * If no file or panel open, disable animation.
   * Animation lasts for 1 second after selection changes.
   * `!` on Tailwind classes: Ant Design Button bg otherwise wins and blink is invisible.
   */
  useEffect(() => {
    let timeoutId: number | undefined;

    if (isRightPanelOpen) {
      setIsDetailsButtonAnimated(false);
    } else if (selectedFiles.length === 1) {
      setIsDetailsButtonAnimated(true);
      timeoutId = window.setTimeout(() => {
        setIsDetailsButtonAnimated(false);
      }, 1000);
    } else {
      setIsDetailsButtonAnimated(false);
    }

    return () => {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [isRightPanelOpen, selectedFiles]);

  /**
   * Effect: Updates right panel content on selection or open.
   * FileDetail mode: 1 → details, >1 → count, 0 → select-item hint (panel stays open).
   * AI document chat mode: 1 → AiDocumentChat, >1 → count placeholder (no composer), 0 → close.
   * When viewer drawer open and list selection empty, `panelFiles` uses drawer file snapshot.
   */
  useEffect(() => {
    if (!isRightPanelOpen) {
      return;
    }

    const action = resolveFilesRightPanelAction(rightPanelKind, panelFiles.length);

    switch (action) {
      case 'aiDocumentChat':
        if (panelFiles[0]) {
          void guardedSetRightPanelContentRef.current(
            <AiDocumentChat key={panelFiles[0].id} file={panelFiles[0]} />,
          );
        }
        break;
      case 'aiSummary':
        if (panelFiles[0]) {
          void guardedSetRightPanelContentRef.current(
            <AiSummary
              key={panelFiles[0].id}
              file={panelFiles[0]}
              onSummaryGenerated={() => {}}
              onRequestClose={() => {
                void guardedCloseRightPanel();
              }}
            />,
          );
        }
        break;
      case 'fileDetail':
        if (panelFiles[0]) {
          void guardedSetRightPanelContentRef.current(<FileDetail file={panelFiles[0]} />);
        }
        break;
      case 'multiSelected':
        void guardedSetRightPanelContentRef.current(
          <div className="px-4 text-xl font-medium">
            {t('common.selected')}: {panelFiles.length}
          </div>,
        );
        break;
      case 'selectItem':
        void guardedSetRightPanelContentRef.current(
          <div className="px-4 text-xl font-medium">{t('common.selectItemToViewDetails')}</div>,
        );
        break;
      case 'close':
        void guardedCloseRightPanel();
        break;
      default:
        break;
    }
  }, [
    guardedCloseRightPanel,
    isRightPanelOpen,
    rightPanelKind,
    selectedFileId,
    panelFiles,
    panelFiles.length,
    t,
  ]);

  /**
   * Effect: If user selects a locked password folder, close the right details panel.
   */
  useEffect(() => {
    if (isLockedSingleSelection && isRightPanelOpen) {
      void guardedCloseRightPanel();
    }
  }, [guardedCloseRightPanel, isLockedSingleSelection, isRightPanelOpen]);

  /**
   * Handler for refresh button: Triggers data reload,
   * resets pagination, and sets a cooldown timer to avoid excessive requests.
   */
  const handleRefresh = () => {
    // Prevent refresh if cooldown is active
    if (folderPasswordRefreshCooldown > 0) {
      return;
    }

    // Start cooldown timer
    setFolderPasswordRefreshCooldown(5);

    // Trigger refresh by resetting offset and adding refreshToken
    onFiltersChange({
      ...filters,
      offset: 0,
      refreshToken: Date.now(),
    });
  };

  return (
    <div className="relative flex w-full flex-col gap-1">
      <div
        className={clsx(
          'flex w-full flex-row items-center justify-between gap-2 rounded-md transition-all duration-200 ease-in-out',
          selectedTableKeys.length > 0 && 'bg-white py-1 shadow-md',
        )}
      >
        {/* If files are selected, show bulk action bar - otherwise show search/filters */}
        {selectedTableKeys.length > 0 ? (
          <FilesActions
            handleFileMenuAction={handleFileMenuAction}
            files={selectedFiles}
            filesActions={filesActions}
          />
        ) : (
          <>
            {/* Desktop: left filters then ListFilter (Activity Log pattern) */}
            <div className="hidden w-full flex-row items-center gap-2 lg:flex">
              {/* fileTypes | shareWith — fileTypes collapses below xl into filters dropdown on Shared */}
              <div className="grid w-full max-w-4xl grid-cols-2 gap-2">
                {filterConfigs
                  .filter((f) => f.group === 'left' && f.show)
                  .map((f) => (
                    <div
                      key={f.key + '-left'}
                      className={clsx(f.hideInlineBelow === 'xl' && 'hidden xl:block')}
                    >
                      {f.render('w-full')}
                    </div>
                  ))}
              </div>
              {isSharedSection && (
                <div className="flex shrink-0 flex-row items-center gap-1">
                  {/* Dropdown: expiry always; fileTypes when collapsed below xl */}
                  <Badge size="small" count={dropdownActiveFilters} offset={[-7, 7]}>
                    <Dropdown
                      trigger={['click']}
                      popupRender={() => (
                        <div
                          className="border-gray-medium grid w-[300px] grid-cols-1 gap-3 rounded-md border bg-white p-2 shadow-md"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-lg font-medium">{t('common.filters')}</span>
                          </div>
                          {filterConfigs
                            .filter(
                              (f) =>
                                f.show && (f.group === 'dropdown' || f.hideInlineBelow === 'xl'),
                            )
                            .map((f) => (
                              <div
                                key={f.key}
                                className={clsx(f.hideInlineBelow === 'xl' && 'xl:hidden')}
                              >
                                {f.render('w-full')}
                              </div>
                            ))}
                        </div>
                      )}
                    >
                      <Button className="flex items-center" aria-label={t('a11y.filters')}>
                        <Icon name="ListFilterIcon" className="w-5" />
                      </Button>
                    </Dropdown>
                  </Badge>
                </div>
              )}

              {filterConfigs
                .filter((f) => f.group === 'right' && f.show)
                .map((f) => (
                  <div key={f.key + '-right'}>{f.render('w-72')}</div>
                ))}
            </div>
            {/* Mobile: all visible filters in dropdown */}
            <div className="z-10 flex items-center justify-start lg:hidden">
              <Badge size="small" count={activeFiltersCount} offset={[0, 7]}>
                <Dropdown
                  trigger={['click']}
                  popupRender={() => (
                    <div
                      className="border-gray-medium grid w-[300px] grid-cols-1 gap-3 rounded-md border bg-white p-2 shadow-md"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-medium">{t('common.filters')}</span>
                      </div>
                      {filterConfigs
                        .filter((f) => f.show)
                        .map((f) => (
                          <React.Fragment key={f.key}>{f.render('w-full')}</React.Fragment>
                        ))}
                    </div>
                  )}
                >
                  <Button className="flex items-center" aria-label={t('a11y.filters')}>
                    <Icon name="ListFilterIcon" className="w-5" />
                  </Button>
                </Dropdown>
              </Badge>
            </div>
          </>
        )}
        <div
          className={clsx('flex shrink-0 flex-row items-center gap-2 transition-all duration-200')}
        >
          {/* Bulk selection clear button */}
          {selectedTableKeys.length > 0 && (
            <div className="border-gray-medium border-r pr-2">
              <Button
                size="small"
                aria-label={t('a11y.clearSelection')}
                onClick={() => {
                  setSelectedTableKeys([]);
                }}
              >
                <span>
                  {t('common.selected')}: {selectedFiles.length}
                </span>
                <Icon name="XIcon" className="w-5" />
              </Button>
            </div>
          )}
          {/* Toggle between grid and list view */}
          <Segmented
            value={isFilesListInGridMode}
            aria-label={t('a11y.viewAsList')}
            onChange={(value) => {
              setSelectedTableKeys([]);
              setIsFilesListInGridMode(value);
            }}
            options={[
              {
                value: false,
                icon: (
                  <Tooltip title={t('a11y.viewAsList')}>
                    <div aria-hidden="true">
                      <Icon name="LayoutListIcon" className="mt-0.5 w-5" />
                    </div>
                  </Tooltip>
                ),
              },

              {
                value: true,
                icon: (
                  <Tooltip title={t('a11y.viewAsGrid')}>
                    <div aria-hidden="true">
                      <Icon name="LayoutGridIcon" className="mt-0.5 w-5" />
                    </div>
                  </Tooltip>
                ),
              },
            ]}
          />
          {/* Details panel toggle */}
          <Tooltip
            title={isLockedSingleSelection ? t('common.unlockFolderToViewDetails') : undefined}
          >
            <span>
              <Button
                className={clsx(
                  'transition-all duration-1000!',
                  isDetailsButtonAnimated && 'bg-azure/50! animate-pulse!',
                )}
                disabled={isLockedSingleSelection}
                onClick={handleToggleRightPanel}
              >
                <Icon
                  name={isRightPanelOpen ? 'PanelRightCloseIcon' : 'PanelRightOpenIcon'}
                  className="w-5 transition-all duration-300"
                />
                <span>{t('common.details')}</span>
              </Button>
            </span>
          </Tooltip>
        </div>
      </div>
      {/* Breadcrumb is in-flow to avoid overlaying the toolbar - ensures correct layout and interaction */}
      <div className="flex min-w-0 flex-1 flex-row items-center gap-2">
        <Breadcrumb
          items={breadcrumbItems}
          className="min-w-0 flex-1 [&>ol]:flex-wrap [&>ol>li]:min-w-0!"
        />
        <div className="flex shrink-0 flex-row items-center justify-end gap-2 px-1">
          {section !== FilesTableEnum.Trash && !isInFileRoute(FilesTableEnum.Trash) && (
            <Dropdown
              menu={{ items: exportMenuItems }}
              trigger={['click']}
              disabled={isExportRunning}
            >
              <Tooltip title={t('files.exportFolderFileList.button')}>
                <Button
                  type="text"
                  size="small"
                  disabled={isExportRunning}
                  loading={isExportRunning}
                  className="flex shrink-0 items-center"
                  aria-label={t('files.exportFolderFileList.button')}
                >
                  <Icon name="FileDownIcon" className="w-5" />
                  <span className="hidden sm:inline">{t('files.exportFolderFileList.button')}</span>
                  <Icon name="ChevronDownIcon" className="hidden w-4 sm:inline" />
                </Button>
              </Tooltip>
            </Dropdown>
          )}
          {/* Folder password refresh (only in Received section) */}
          {isInFileRoute(FilesTableEnum.Received) && (
            <Tooltip
              title={
                folderPasswordRefreshCooldown > 0
                  ? t('common.folderPasswordRefreshCooldown', {
                      seconds: folderPasswordRefreshCooldown,
                    })
                  : t('common.refresh')
              }
            >
              <Button
                type="text"
                size="small"
                loading={isLoading}
                disabled={folderPasswordRefreshCooldown > 0}
                onClick={handleRefresh}
                className="flex shrink-0 items-center"
              >
                <Icon name="RotateCwIcon" className="w-5" />
                {t('common.refresh')}
                {folderPasswordRefreshCooldown > 0 && ` ${folderPasswordRefreshCooldown}s`}
              </Button>
            </Tooltip>
          )}
          {/* Empty trash (only shows in Trash when there are files) */}
          {isInFileRoute(FilesTableEnum.Trash) && filesCount > 0 && (
            <Button
              onClick={() => {
                handleFileMenuAction([], FileMenuActionEnum.EmptyTrash);
              }}
              size="small"
              type="text"
              danger
            >
              <Icon name="TrashIcon" className="w-5" />
              {t('common.emptyTrash')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilesHeader;
